import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Download, Filter, FileSpreadsheet, ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { forwardLeadToWhatsApp } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: Search, t: "Extract Business Leads", d: "Pull thousands of business listings from Google in minutes — by city, category or keyword." },
  { icon: Filter, t: "Lead Filtering", d: "Filter by ratings, reviews count, contact availability and category." },
  { icon: Download, t: "Bulk Download", d: "Export to CSV, Excel or directly into your CRM." },
  { icon: FileSpreadsheet, t: "Detailed Records", d: "Name, address, phone, website, ratings, hours — all in one row." },
];

const PLANS = [
  { name: "All-Access", price: "₹499", period: "/month", limit: "Unlimited extracts" },
];

export default function GoogleExtractor() {
  const [lead, setLead] = useState({ name: "", email: "", phone: "", plan: "All-Access" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const buy = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email || !lead.phone) {
      toast.error("Please fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        marketplace: "Google Extractor",
        message: `Interested in Google Business Data Extractor — ${lead.plan} plan`,
        source: "google_extractor",
      };
      await axios.post(`${API}/contact`, payload);
      setDone(true);
      toast.success("Got it! Opening WhatsApp for payment & access...");
      forwardLeadToWhatsApp(payload);
    } catch {
      toast.error("Submission failed. Try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Google Business Data Extractor — Extract Leads in Minutes"
        description="Premium Google Business Data Extractor tool. Extract thousands of business leads, filter by category and city, export to CSV. Built for sales teams in India."
        path="/google-extractor"
      />

      <section className="border-b-2 border-black bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-7">
            <div className="tape">SaaS Tool</div>
            <h1 className="display mt-6 text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
              Google Business<br />
              <span className="bg-[#FFD700] px-2">Data Extractor.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-black/70 md:text-lg">
              Extract thousands of verified business leads from Google in minutes. Built for sales, agencies, and B2B teams.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#pricing" className="btn-brutal" data-testid="extractor-cta-buy">
                Buy Now <ArrowUpRight size={16} />
              </a>
              <Link to="/contact/?type=extractor-demo" className="btn-brutal secondary">
                Request Demo
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-bold uppercase tracking-widest text-black/60">
              <span className="flex items-center gap-1"><CheckCircle2 size={14} /> Monthly subscription</span>
              <span className="flex items-center gap-1"><CheckCircle2 size={14} /> CSV / Excel export</span>
              <span className="flex items-center gap-1"><CheckCircle2 size={14} /> No code</span>
            </div>
          </div>

          {/* Mock SaaS UI screenshot */}
          <div className="md:col-span-5">
            <div className="card-brutal p-0">
              <div className="border-b-2 border-black bg-black px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F56]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FFBD2E]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#27C93F]" />
                  <span className="ml-3 text-[10px] font-bold uppercase tracking-widest text-white/70">extractor.orina.app</span>
                </div>
              </div>
              <div className="bg-white p-5">
                <div className="flex gap-2">
                  <div className="flex-1 border-2 border-black bg-[#FAFAF7] px-3 py-2 text-xs">
                    <span className="text-black/50">search:</span> dentists in mumbai
                  </div>
                  <div className="border-2 border-black bg-[#FFD700] px-3 py-2 text-xs font-bold">
                    Run
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] font-bold uppercase tracking-widest text-black/50">
                  <div>Found</div>
                  <div>Cities</div>
                  <div>Rating ≥</div>
                </div>
                <div className="mt-1 grid grid-cols-3 gap-2 text-sm font-black">
                  <div>2,184</div>
                  <div>Mumbai +12</div>
                  <div>4.0+</div>
                </div>
                <div className="mt-4 space-y-1 border-2 border-black bg-[#FAFAF7] p-3">
                  {[
                    { n: "Bright Smile Dental", r: "4.7" },
                    { n: "Dr. Patel Clinic", r: "4.6" },
                    { n: "Smiles & Co.", r: "4.5" },
                    { n: "Dental Pro Mumbai", r: "4.5" },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-black/10 pb-1 text-xs last:border-0">
                      <span className="font-bold">{row.n}</span>
                      <span className="text-black/60">★ {row.r}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="font-bold uppercase tracking-widest text-black/50">2,184 results</span>
                  <span className="border-2 border-black bg-[#FFD700] px-3 py-1 font-bold">Export CSV</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">Features</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">Built for fast B2B prospecting.</h2>
          <div className="mt-10 grid grid-cols-1 gap-0 md:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => (
              <div key={f.t} className="-ml-[2px] -mt-[2px] border-2 border-black bg-white p-7 hover:bg-[#FFD700]">
                <div className="flex h-12 w-12 items-center justify-center border-2 border-black bg-[#FFD700]">
                  <f.icon size={20} />
                </div>
                <h3 className="display mt-5 text-lg font-black">{f.t}</h3>
                <p className="mt-2 text-sm text-black/70">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-y-2 border-black bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center">
            <div className="eyebrow">Simple Pricing</div>
            <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">One plan. Everything included.</h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-black/70">
              No tiers, no upgrade nags. ₹499/month gets you the whole tool.
            </p>
          </div>
          <div className="mx-auto mt-12 max-w-xl">
            <div
              data-testid="extractor-plan-all-access"
              className="relative border-2 border-black bg-[#FFD700] p-8 shadow-brutal-lg md:p-10"
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 border-2 border-black bg-black px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">
                All-Access · ₹499/month
              </div>
              <div className="flex items-end justify-center gap-1">
                <div className="display text-7xl font-black md:text-8xl">₹499</div>
                <div className="pb-3 text-sm font-bold uppercase tracking-widest text-black/60">/ month</div>
              </div>
              <div className="mt-2 text-center text-sm font-bold text-black/70">Unlimited extracts · cancel anytime</div>

              <ul className="mx-auto mt-8 grid max-w-md grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> Extract business leads</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> Filter by rating & city</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> CSV / Excel export</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> Bulk downloads</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> Phone, email, website</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> Ratings & reviews</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0" /> Email + WhatsApp support</li>
                <li className="flex items-start gap-2"><Sparkles size={14} className="mt-0.5 shrink-0" /> Priority access</li>
              </ul>

              <button
                data-testid="extractor-buy-all-access"
                onClick={() => {
                  setLead((l) => ({ ...l, plan: "All-Access" }));
                  document.getElementById("buy-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-brutal dark mt-8 w-full"
              >
                Get Access — ₹499/month
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* BUY FORM */}
      <section id="buy-form" className="bg-[#FAFAF7] py-16">
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          <div className="card-brutal p-6 md:p-10">
            {done ? (
              <div data-testid="extractor-done" className="text-center">
                <div className="display text-3xl font-black md:text-4xl">Thanks 🎉</div>
                <p className="mt-3 text-base text-black/70">
                  We'll reach out shortly with payment and access details for the <strong>{lead.plan}</strong> plan.
                </p>
              </div>
            ) : (
              <form onSubmit={buy} className="space-y-3" data-testid="extractor-buy-form">
                <h3 className="display text-2xl font-black md:text-3xl">Buy {lead.plan} plan</h3>
                <p className="text-sm text-black/70">We'll send payment & access details on email + WhatsApp.</p>
                <input
                  required
                  data-testid="extractor-name"
                  placeholder="Full name"
                  value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  className="w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                />
                <input
                  required
                  type="email"
                  data-testid="extractor-email"
                  placeholder="Email"
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  className="w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                />
                <input
                  required
                  data-testid="extractor-phone"
                  placeholder="Phone (+91...)"
                  value={lead.phone}
                  onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                  className="w-full border-2 border-black bg-white px-3 py-2.5 outline-none focus:bg-[#FFFBE6]"
                />
                <div className="border-2 border-black bg-[#FFD700] p-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-black/70">You're getting</div>
                  <div className="display text-2xl font-black">All-Access · ₹499/month</div>
                </div>
                <button
                  disabled={submitting}
                  type="submit"
                  data-testid="extractor-submit"
                  className="btn-brutal w-full disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Get Access"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Want a custom data-extraction project?"
        subtitle="Need 50,000+ leads or a custom Google scraping setup? Talk to our SaaS team."
        primaryLabel="Talk to SaaS Team"
        primaryTo="/contact/?type=extractor"
      />
    </>
  );
}
